import { RequestCareProgramRequest } from 'services/careProgramRequestService';
import {
  CareProgramAPIResponse,
  CareProgramCommunicationMethod,
  CareProgramEnrollmentRequest,
  CareProgramEnrollmentResponse,
  CareProgramEnrollmentStatus,
  CareProgramEnrollmentType,
} from '../types';

const ENROLLMENT_TYPES: CareProgramEnrollmentType[] = ['self', 'care_team_recommended'];
const COMMUNICATION_METHODS: CareProgramCommunicationMethod[] = ['sms', 'email', 'app'];
const ENROLLMENT_STATUSES: CareProgramEnrollmentStatus[] = ['pending', 'approved', 'active'];

const isNonEmptyString = (value: unknown): value is string =>
  typeof value === 'string' && value.trim().length > 0;

const isValidNumber = (value: unknown): value is number =>
  typeof value === 'number' && !Number.isNaN(value) && value > 0;

const isValidISODate = (value: unknown): boolean =>
  isNonEmptyString(value) && !Number.isNaN(new Date(value).getTime());

export const isValidCareProgramEnrollmentRequest = (request: RequestCareProgramRequest): boolean =>
  isNonEmptyString(request.memberUuid) && isValidNumber(request.programId);

/** Type guard for enroll success response shape. */
export const isCareProgramEnrollmentResponse = (
  data: unknown,
): data is CareProgramEnrollmentResponse => {
  if (typeof data !== 'object' || data === null) return false;
  const response = data as Partial<CareProgramEnrollmentResponse>;

  return (
    isNonEmptyString(response.enrollmentId) &&
    isNonEmptyString(response.enrolledAt) &&
    ENROLLMENT_STATUSES.includes(response.status as CareProgramEnrollmentStatus) &&
    typeof response.programDetails === 'object' &&
    response.programDetails !== null &&
    isNonEmptyString(response.programDetails.id) &&
    isNonEmptyString(response.programDetails.name) &&
    typeof response.programDetails.description === 'string' &&
    Array.isArray(response.nextSteps) &&
    response.nextSteps.every(
      (step) =>
        typeof step === 'object' &&
        step !== null &&
        isNonEmptyString(step.action) &&
        isNonEmptyString(step.description) &&
        isNonEmptyString(step.dueDate),
    )
  );
};

/**
 * Derives enrollment status from a care program API response.
 * @param program - The care program API response
 * @returns Object with enrollment status flags
 */
export const deriveEnrollmentStatus = (
  program: CareProgramAPIResponse,
): { isEnrolled: boolean; hasRequestPending: boolean } => {
  // Check if there's an active subscription (not ended)
  const isEnrolled = program.care_program_subscriptions?.some((sub) => !sub.is_ended) ?? false;

  // Check if there's a pending enrollment request
  const hasRequestPending = isNonEmptyString(program.care_program_request_id);

  return {
    isEnrolled,
    hasRequestPending,
  };
};
